/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Text } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import type { Deal } from '@/services/requests/housien-services/DealsService';
import { useLostReasonLabels, type LostReason } from '../constants/lost-reasons';
import { handoffFailureFrom, handoffIdFrom } from '../utils/deal-events';
import {
  ACCENT_BG_CLASS,
  ACCENT_BORDER_CLASS,
  ACCENT_SOFT_BG_CLASS,
  ACCENT_TEXT_CLASS,
  acceptedOfferValue,
  formatDealMoney,
  type DealViewState,
} from '../utils/deal-state';

interface DealOutcomeCardProps {
  agencyId: string;
  deal: Deal;
  view: DealViewState;
}

/** Where a won deal's follow-up record lives, per deal type. */
interface HandoffTarget {
  id: string;
  labelKey: string;
  to: string;
}

/**
 * The record the win handed the deal off to, read from the event the
 * backend wrote when it created it. Absent when that creation failed —
 * the matching `*_creation_failed` event tells that story in the timeline.
 *
 * @param agencyId - The owning agency.
 * @param deal - The won deal.
 * @returns The handoff link target, or null when there is none.
 */
function handoffTargetOf(agencyId: string, deal: Deal): HandoffTarget | null {
  const isRental = deal.deal_type === 'rental';
  const type = isRental ? 'lease_draft_created' : 'sale_record_created';
  const id = handoffIdFrom(deal.events, type);
  if (!id) return null;
  return isRental
    ? { id, labelKey: 'detail.outcome.openLease', to: `/app/agencies/${agencyId}/leases/${id}` }
    : { id, labelKey: 'detail.outcome.openSale', to: `/app/agencies/${agencyId}/sales/${id}` };
}

/**
 * Body copy of a won outcome: the accepted value and commission, plus
 * the note about the record that opened automatically.
 */
function useWonBody(deal: Deal): string {
  const { t } = useTranslation('features/deals');
  const value = formatDealMoney(acceptedOfferValue(deal)) ?? '—';
  const commission = deal.commission?.total_percentage;
  const head = commission != null
    ? t('detail.outcome.wonBody', { value, commission })
    : t('detail.outcome.wonBodyNoCommission', { value });
  const tail = deal.deal_type === 'rental'
    ? t('detail.outcome.wonLeaseNote')
    : t('detail.outcome.wonSaleNote');
  return `${head} ${tail}`;
}

/**
 * Title and body for whichever outcome the deal reached.
 */
function useOutcomeCopy(deal: Deal, view: DealViewState): { title: string; body: string } {
  const { t } = useTranslation('features/deals');
  const reasonLabels = useLostReasonLabels();
  const wonBody = useWonBody(deal);
  if (view.isWon) {
    return {
      title: deal.deal_type === 'rental'
        ? t('detail.outcome.wonRental')
        : t('detail.outcome.wonSale'),
      body: wonBody,
    };
  }
  const reason = deal.lost_reason
    ? reasonLabels[deal.lost_reason as LostReason] ?? t('lostReasons.outro')
    : t('lostReasons.outro');
  return {
    title: t('detail.outcome.lost', { reason: reason.toLowerCase() }),
    body: deal.lost_note || t('detail.outcome.lostNoteEmpty'),
  };
}

/**
 * The desfecho card shown once a deal is terminal: what the outcome was,
 * the link to the record the win opened, and the way to start a fresh
 * attendance for the same client. The deal itself is read-only here.
 *
 * @param props - The deal, its derived view state, and the new-attendance
 *   callback.
 * @returns The outcome card.
 */
export function DealOutcomeCard({ agencyId, deal, view }: DealOutcomeCardProps) {
  const { t } = useTranslation('features/deals');
  const { title, body } = useOutcomeCopy(deal, view);
  const handoff = view.isWon ? handoffTargetOf(agencyId, deal) : null;
  // Only worth showing when the link it replaces is missing: a failure
  // followed by a successful retry leaves both events on the timeline.
  const handoffFailed = view.isWon && !handoff
    && handoffFailureFrom(deal.events) !== null;

  return (
    <div
      data-testid="deal-outcome-card"
      className={cn(
        'flex flex-col gap-(--space-2-5) rounded-(--radius-lg) border p-(--space-4)',
        ACCENT_BORDER_CLASS[view.accent],
        ACCENT_SOFT_BG_CLASS[view.accent],
      )}
    >
      <div className="flex items-center gap-(--space-2)">
        <div className={cn('h-2 w-2 shrink-0 rounded-full', ACCENT_BG_CLASS[view.accent])} />
        <Text component="span" className="text-[length:var(--text-app-heading)] [font-weight:var(--font-semibold)] text-(--text-primary)">
          {title}
        </Text>
      </div>

      <Text component="span" className="text-[length:var(--text-body-sm)] leading-normal text-(--text-secondary)">
        {body}
      </Text>

      {handoff && (
        <Link
          to={handoff.to}
          data-testid="deal-handoff-link"
          className={cn(
            'w-fit rounded-(--radius-md) border bg-(--surface-card) px-(--space-3-5) py-(--space-2-5)',
            'text-[length:var(--text-body-sm)] [font-weight:var(--font-semibold)] no-underline',
            ACCENT_BORDER_CLASS[view.accent],
            ACCENT_TEXT_CLASS[view.accent],
          )}
        >
          {t(handoff.labelKey, { id: handoff.id })}
        </Link>
      )}

      {handoffFailed && (
        <Text
          component="span"
          data-testid="deal-handoff-failed-note"
          className="text-[length:var(--text-body-sm)] leading-normal text-(--accent-cobre)"
        >
          {t('detail.outcome.handoffFailed')}
        </Text>
      )}

      <Text component="span" className="text-[length:var(--text-meta)] text-(--text-tertiary)">
        {t('detail.outcome.readOnly')}
      </Text>
    </div>
  );
}
